import { useCallback, useState } from 'react';
import { ClockCounterClockwise, LinkBreak, PencilSimple, UserPlus } from '@phosphor-icons/react';
import { BestaetigenDialog, Chip, ImmoButton, InnenKarte, KartenTitel, LeerZustand, SearchSelect, ToastPill, useToastPill, type SucheOption } from '../../../../../components/immobilien/ui';
import { eur } from '../../../../../lib/immobilien/format';
import { deDatum } from '../../../../../lib/immobilien/objektDetail';
import { mietdauer, type MvEintrag } from '../../../../../lib/immobilien/einheitDetail';
import { useOpenKontakt } from '../../../../../lib/immobilien/openKontakt';
import { bereinigeSuchbegriff } from '../../../../../lib/kontakte/personenSuche';
import { kontaktName } from '../../../../../lib/objekte/objektKontakte';
import { supabase } from '../../../../../lib/supabase/client';
import { useEinheitSeite } from '../EinheitSeitenKontext';

interface MieterTreffer { id: string; vorname: string | null; nachname: string | null; firma_name: string | null; email: string | null; einheit: string | null; einheit_id: string | null }

const STATUS: Record<MvEintrag['status'], { ton: 'ok' | 'warn' | 'neutral'; text: string }> = {
  aktiv: { ton: 'ok', text: 'Aktiv' }, gekuendigt: { ton: 'warn', text: 'Gekündigt' }, ehemalig: { ton: 'neutral', text: 'Ehemalig' },
};

/**
 * „Mietverhältnisse“: Zeitstrahl aller Mietverhältnisse der Einheit. Wie bisher: bearbeiten
 * (Mietverhältnis-Dialog), von der Einheit lösen (Kontakt bleibt erhalten) und einen bestehenden
 * Mieter-Kontakt der Einheit zuordnen.
 */
export function MvBereich() {
  const s = useEinheitSeite();
  const openKontakt = useOpenKontakt();
  const [toast, zeige] = useToastPill();
  const [loesen, setLoesen] = useState<MvEintrag | null>(null);
  const [zuordnen, setZuordnen] = useState<MieterTreffer | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const e = s.daten.einheit;
  const bezeichnung = (e.bezeichnung ?? '').trim();
  const eigeneIds = new Set(s.daten.mieter.map(m => m.id));

  const sucheMieter = useCallback(async (anfrage: string, _typ: string | null, signal: AbortSignal): Promise<SucheOption[]> => {
    const muster = `%${bereinigeSuchbegriff(anfrage).replace(/%/g, ' ')}%`;
    const { data, error } = await supabase.from('mieter').select('id, vorname, nachname, firma_name, email, einheit, einheit_id')
      .or(`vorname.ilike.${muster},nachname.ilike.${muster},firma_name.ilike.${muster},email.ilike.${muster}`)
      .order('nachname').limit(10).abortSignal(signal);
    if (error) throw new Error(error.message);
    return ((data ?? []) as MieterTreffer[]).filter(m => !eigeneIds.has(m.id)).slice(0, 8).map(m => ({
      id: m.id, label: kontaktName(m), typ: 'mieter',
      sub: [m.email, m.einheit ? `aktuell: ${m.einheit}` : ''].filter(Boolean).join(' · ') || undefined,
    }));
  }, [eigeneIds]);

  const gewaehlt = async (o: SucheOption) => {
    const { data, error } = await supabase.from('mieter').select('id, vorname, nachname, firma_name, email, einheit, einheit_id').eq('id', o.id).maybeSingle();
    if (error || !data) { zeige(`Mieter nicht gefunden${error ? `: ${error.message}` : ''}`, true); return; }
    setZuordnen(data as MieterTreffer);
  };

  const bestaetigeZuordnen = async () => {
    if (!zuordnen) return;
    setLaeuft(true);
    const { error } = await supabase.from('mieter').update({ einheit: bezeichnung || null, einheit_id: e.id, objekt_id: s.daten.objekt.id }).eq('id', zuordnen.id);
    setLaeuft(false);
    if (error) { zeige(`Nicht zugeordnet: ${error.message}`, true); return; }
    zeige(`${kontaktName(zuordnen)} zugeordnet`);
    setZuordnen(null);
    s.geaendert('mieter');
  };

  const bestaetigeLoesen = async () => {
    if (!loesen) return;
    setLaeuft(true);
    const { error } = await supabase.from('mieter').update({ einheit: null, einheit_id: null }).eq('id', loesen.id);
    setLaeuft(false);
    if (error) { zeige(`Nicht gelöst: ${error.message}`, true); return; }
    zeige(`${loesen.name} von der Einheit gelöst`);
    setLoesen(null);
    s.geaendert('mieter');
  };

  const anderswo = zuordnen && (zuordnen.einheit_id ? zuordnen.einheit_id !== e.id : !!zuordnen.einheit && zuordnen.einheit !== bezeichnung);

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte>
        <KartenTitel icon={UserPlus}>Mieter zuordnen</KartenTitel>
        <SearchSelect label="Bestehenden Mieter-Kontakt suchen" platzhalter="Name, Firma oder E-Mail …" suche={sucheMieter} onWahl={o => { void gewaehlt(o); }} />
        <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Neue Mieter legst du im Kontaktbereich an oder direkt beim Anlegen einer Einheit („Vermietet“).</span>
      </InnenKarte>

      {s.zeitstrahl.length === 0 ? (
        <LeerZustand icon={ClockCounterClockwise} titel="Noch kein Mietverhältnis" text="Ordne oben einen Mieter zu." />
      ) : (
        <div className="flex flex-col" style={{ gap: 10 }}>
          {s.zeitstrahl.map(z => {
            const m = s.daten.mieter.find(x => x.id === z.id);
            const mit = s.daten.mitmieter.filter(x => x.hauptId === z.id).map(kontaktName);
            const st = STATUS[z.status];
            return (
              <div key={z.id} className="immo-innen flex items-center flex-wrap" style={{ borderRadius: 20, padding: '14px 16px', gap: 14 }}>
                <span aria-hidden className="shrink-0" style={{ width: 12, height: 12, borderRadius: '50%', background: z.status === 'aktiv' ? 'var(--ok)' : z.status === 'gekuendigt' ? 'var(--warn)' : 'var(--ink3)' }} />
                <div className="flex flex-col flex-1 min-w-0" style={{ gap: 2, flexBasis: 220 }}>
                  <button type="button" onClick={() => openKontakt(z.id, 'mieter')} className="truncate text-left cursor-pointer"
                    style={{ border: 'none', background: 'transparent', padding: 0, fontSize: 15, fontWeight: 800, color: 'var(--ink)' }}>
                    {z.name}{mit.length ? `, ${mit.join(', ')}` : ''}
                  </button>
                  <span className="immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>
                    {z.von ? `${deDatum(z.von)}${z.bis ? ` – ${deDatum(z.bis)}` : ''}` : 'ohne Mietbeginn'}
                    {z.von && z.status !== 'ehemalig' ? ` · ${mietdauer(z.von, s.heute)}` : ''}
                  </span>
                </div>
                <Chip groesse="xs" ton={st.ton}>{st.text}</Chip>
                <span className="immo-zahl" style={{ fontSize: 14, fontWeight: 800, minWidth: 110, textAlign: 'right' }}>{eur(z.gesamt)} / Monat</span>
                <div className="flex" style={{ gap: 6 }}>
                  {m && <ImmoButton groesse={32} icon={PencilSimple} onClick={() => s.bearbeiteMietverhaeltnis(m)}>Bearbeiten</ImmoButton>}
                  <ImmoButton groesse={32} art="bad" icon={LinkBreak} aria-label={`${z.name} von der Einheit lösen`} title="Von der Einheit lösen" onClick={() => setLoesen(z)} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {zuordnen && (
        <BestaetigenDialog
          titel="Mieter zuordnen?"
          text={anderswo ? `${kontaktName(zuordnen)} ist aktuell „${zuordnen.einheit ?? 'einer anderen Einheit'}“ zugewiesen. Stattdessen „${bezeichnung}“ zuweisen?` : `${kontaktName(zuordnen)} wird der Einheit „${bezeichnung}“ zugewiesen.`}
          bestaetigenLabel="Zuordnen"
          icon={UserPlus}
          gefahr={false}
          laeuft={laeuft}
          onBestaetigen={() => { void bestaetigeZuordnen(); }}
          onAbbrechen={() => setZuordnen(null)}
        />
      )}
      {loesen && (
        <BestaetigenDialog
          titel="Mietverhältnis lösen?"
          text={`Der Kontakt „${loesen.name}“ bleibt erhalten, wird aber von „${bezeichnung}“ getrennt.`}
          bestaetigenLabel="Lösen"
          icon={LinkBreak}
          laeuft={laeuft}
          onBestaetigen={() => { void bestaetigeLoesen(); }}
          onAbbrechen={() => setLoesen(null)}
        />
      )}
    </>
  );
}
